import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import { GhostSuccessButton } from 'ui/button';
import { AddNew16, NegativeFill } from 'ui/icons';
import { colors } from 'ui/theme';
import { arrayMoveImmutable } from 'utils/arrayMove';

import { Button, EmbeddedContentDefinition } from 'modules/graphql';
import { FieldNames, getIsEditMode } from 'modules/templateEditor/common';
import { useEditableFieldsContext } from 'modules/templateEditor/context/EditableFieldsContext';
import { useQueryParamContext } from 'modules/templateEditor/context/QueryParamContext';
import { useValidateContentField } from 'modules/templateEditor/hooks/useValidateContentField';

import { usePlacementSelectorContext } from 'modules/placements/context/PlacementSelectorContext';
import { DISPLAY_CONTENT, EmbeddedActionButton } from '../../constants';
import { EmptyInput, ReadOnlyField, getInvalidButtonActionsFromContentErrors } from '../../utils';
import { StyledFormField } from '../components/styles';
import { ActionButtonTile } from './ActionButtonTile';

const { actionButtonCreateButton, actionButtonDefaultTitle, formActionButtonsLabel } =
  DISPLAY_CONTENT.settings;

const defaultActionButton: Button = {
  id: '',
  title: actionButtonDefaultTitle,
  action: {
    type: EmbeddedActionButton.URL,
    data: '',
  },
};

export const ActionButtons = () => {
  const { view } = useQueryParamContext();
  const { isEditMode, liveTemplate, setCurrentMediumField } = useEditableFieldsContext();
  const isEditModeVisible = getIsEditMode(view, isEditMode);
  const { elements } = liveTemplate.mediumData as EmbeddedContentDefinition;
  const templateActionButtons = useMemo(() => elements?.buttons || [], [elements]);

  const { selectedPlacementDetails } = usePlacementSelectorContext();
  const placementActionButtons = selectedPlacementDetails?.actionButtons;
  const shouldDisableAddButton = templateActionButtons?.length === placementActionButtons?.length;

  const { localeContentErrors } = useValidateContentField(FieldNames.ACTION_BUTTONS);
  const buttonActionErrors = getInvalidButtonActionsFromContentErrors(localeContentErrors);
  const [hasErrorButtonIndex, setHasErrorButtonIndex] = useState<boolean[]>([]);

  const setActionButtons = (updatedActions: Button[]) => {
    const newElements = {
      ...elements,
      buttons: updatedActions,
    };
    setCurrentMediumField('elements', newElements);
  };

  const handleAddButton = () => {
    const currButtonsLength = templateActionButtons.length;
    const newButton = placementActionButtons?.[currButtonsLength] ?? defaultActionButton;
    setActionButtons([...templateActionButtons, newButton]);
  };

  const sortActionButtonList = useCallback(
    (dragEndObj: any) => {
      const sourceIndex = dragEndObj.source.index;
      const destinationIndex = dragEndObj.destination?.index;

      if (typeof sourceIndex === 'number' && typeof destinationIndex === 'number') {
        const newList = arrayMoveImmutable(
          [...templateActionButtons],
          sourceIndex,
          destinationIndex,
        );
        setActionButtons(newList);
      }
    },
    [templateActionButtons],
  );

  const ReadOnlyContent = templateActionButtons?.length ? (
    templateActionButtons.map((button, index) => (
      <Fragment key={`action-button-readonly-${index}`}>
        {index > 0 && ', '}
        {hasErrorButtonIndex[index] && <NegativeFill color={colors.negative100} />}
        {button.title}
      </Fragment>
    ))
  ) : (
    <EmptyInput />
  );

  useEffect(() => {
    setHasErrorButtonIndex(
      templateActionButtons.map((button) =>
        buttonActionErrors.some(
          ({ type, data }) => button.action?.type === type && button.action?.data === data,
        ),
      ),
    );
  }, [templateActionButtons, localeContentErrors]);

  return (
    <StyledFormField
      data-test="embedded-template-settings-form-action-buttons"
      labelText={formActionButtonsLabel}
      isHorizontalLayout
    >
      {isEditModeVisible ? (
        <>
          <DragDropContext onDragEnd={sortActionButtonList}>
            <Droppable droppableId="action-buttons-droppable">
              {(droppableProvided) => (
                <div {...droppableProvided.droppableProps} ref={droppableProvided.innerRef}>
                  {templateActionButtons.map((button: Button, index: number) => (
                    <Draggable key={`item-${index}`} draggableId={`item-${index}`} index={index}>
                      {(draggableProvided) => (
                        <div
                          ref={draggableProvided.innerRef}
                          {...draggableProvided.draggableProps}
                          {...draggableProvided.dragHandleProps}
                        >
                          <ActionButtonTile
                            key={`action-button-${index}`}
                            button={button}
                            hasError={hasErrorButtonIndex[index]}
                            index={index}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          <GhostSuccessButton
            data-test="action-buttons-add-button"
            isDisabled={shouldDisableAddButton}
            onClick={handleAddButton}
          >
            <AddNew16 />
            {actionButtonCreateButton}
          </GhostSuccessButton>
        </>
      ) : (
        <ReadOnlyField>{ReadOnlyContent}</ReadOnlyField>
      )}
    </StyledFormField>
  );
};
